import React, { useState, useEffect, useContext, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useHistory } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";

const COLUMNS = [
  { status: "pending", labelKey: "ticketsList.pendingHeader" },
  { status: "open", labelKey: "ticketsList.assignedHeader" },
  { status: "paused", labelKey: "tickets.tabs.paused.title" },
  { status: "closed", labelKey: "tickets.tabs.closed.title" },
];

const useStyles = makeStyles((theme) => ({
  board: {
    display: "flex",
    height: "100%",
    overflowX: "auto",
    padding: theme.spacing(1),
    gap: theme.spacing(1),
  },
  column: {
    display: "flex",
    flexDirection: "column",
    minWidth: 280,
    maxWidth: 280,
    height: "100%",
    backgroundColor: theme.palette.background.default,
  },
  columnHeader: {
    padding: theme.spacing(1, 2),
    fontWeight: 600,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  columnBody: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1),
    ...theme.scrollbarStyles,
  },
  card: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    cursor: "grab",
  },
  cardBody: {
    flex: 1,
    minWidth: 0,
  },
  cardHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cardMessage: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
}));

const buildStatusMap = () =>
  COLUMNS.reduce((acc, { status }) => ({ ...acc, [status]: [] }), {});

const fetchColumn = async (status, queueIds) => {
  const { data } = await api.get("/tickets", {
    params: { status, showAll: true, pageNumber: 1, queueIds },
  });
  return data.tickets;
};

const nextUserIdFor = (status, currentUserId, actorId) => {
  if (status === "open") return actorId;
  if (status === "pending") return null;
  return currentUserId ?? null;
};

const KanbanBoard = ({ selectedQueueIds }) => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [columns, setColumns] = useState(buildStatusMap);

  const reload = useCallback(async () => {
    const queueIds = JSON.stringify(selectedQueueIds);
    try {
      const results = await Promise.all(
        COLUMNS.map(({ status }) => fetchColumn(status, queueIds))
      );
      const next = buildStatusMap();
      COLUMNS.forEach(({ status }, index) => {
        next[status] = results[index];
      });
      setColumns(next);
    } catch (err) {
      toastError(err);
    }
  }, [selectedQueueIds]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const socket = openSocket();

    COLUMNS.forEach(({ status }) => socket.emit("joinTickets", status));

    const upsertTicket = (ticket) => {
      setColumns((prev) => {
        const next = {};
        COLUMNS.forEach(({ status }) => {
          next[status] = prev[status].filter((t) => t.id !== ticket.id);
        });
        if (next[ticket.status]) {
          next[ticket.status] = [ticket, ...next[ticket.status]];
        }
        return next;
      });
    };

    const removeTicket = (ticketId) => {
      setColumns((prev) => {
        const next = {};
        COLUMNS.forEach(({ status }) => {
          next[status] = prev[status].filter((t) => t.id !== ticketId);
        });
        return next;
      });
    };

    socket.on("connect", () => {
      COLUMNS.forEach(({ status }) => socket.emit("joinTickets", status));
    });

    socket.on("ticket", (data) => {
      if (data.action === "update") upsertTicket(data.ticket);
      if (data.action === "delete") removeTicket(data.ticketId);
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;

    const ticketId = Number(draggableId);
    const fromStatus = source.droppableId;
    const toStatus = destination.droppableId;
    const ticket = columns[fromStatus].find((t) => t.id === ticketId);
    if (!ticket) return;

    setColumns((prev) => {
      const next = { ...prev };
      next[fromStatus] = prev[fromStatus].filter((t) => t.id !== ticketId);
      next[toStatus] = [
        { ...ticket, status: toStatus },
        ...prev[toStatus],
      ];
      return next;
    });

    try {
      await api.put(`/tickets/${ticketId}`, {
        status: toStatus,
        userId: nextUserIdFor(toStatus, ticket.userId, user?.id),
      });
    } catch (err) {
      toastError(err);
      reload();
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className={classes.board}>
        {COLUMNS.map(({ status, labelKey }) => (
          <Paper key={status} variant="outlined" className={classes.column}>
            <div className={classes.columnHeader}>
              <Badge
                overlap="rectangular"
                badgeContent={columns[status].length}
                color="primary"
              >
                {i18n.t(labelKey)}
              </Badge>
            </div>
            <Droppable droppableId={status}>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={classes.columnBody}
                >
                  {columns[status].map((ticket, index) => (
                    <Draggable
                      key={ticket.id}
                      draggableId={String(ticket.id)}
                      index={index}
                    >
                      {(dragProvided) => (
                        <Paper
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          variant="outlined"
                          className={classes.card}
                          onClick={() => history.push(`/tickets/${ticket.id}`)}
                        >
                          <Avatar src={ticket?.contact?.profilePicUrl} />
                          <div className={classes.cardBody}>
                            <div className={classes.cardHeaderRow}>
                              <Typography noWrap variant="body2">
                                {ticket.contact?.name}
                              </Typography>
                              {ticket.updatedAt && (
                                <Typography variant="caption" color="textSecondary">
                                  {isSameDay(parseISO(ticket.updatedAt), new Date())
                                    ? format(parseISO(ticket.updatedAt), "HH:mm")
                                    : format(parseISO(ticket.updatedAt), "dd/MM")}
                                </Typography>
                              )}
                            </div>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              className={classes.cardMessage}
                            >
                              {ticket.lastMessage}
                            </Typography>
                          </div>
                        </Paper>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </Paper>
        ))}
      </div>
    </DragDropContext>
  );
};

export default KanbanBoard;
